/**
 * Formatting utilities for shared display logic.
 * Place all new formatting-related pure functions here.
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  custom,
  LATEST_WORD
} from '@portal/app/shared/services/date-time.service';

dayjs.extend(relativeTime);

const dayFormatter = new Intl.NumberFormat('en-US', {
  style: 'unit',
  unit: 'day'
});

export function formatDays(days: number) {
  return dayFormatter.format(days);
}

/**
 * Reference period is typically the relative day string (e.g., 'Latest 30 Days', 'Custom'), not a date range.
 * For 'Custom', we currently display the date range, but this should be verified with Product/UX if that's the intended behavior.
 * For all other cases, replace 'Latest' with 'last'.
 */
export function formatReferencePeriodDisplay(ref: {
  label: string;
  startDate: string;
  endDate: string;
  optimizedForDays: number;
  planStartDate?: string;
  planEndDate?: string;
}): string {
  if (!ref) return '';
  const isSameStartDayLastYear = dayjs(ref.startDate)
    .add(1, 'year')
    .isSame(dayjs(ref.planStartDate), 'day');
  const isSameEndDayLastYear = dayjs(ref.endDate)
    .add(1, 'year')
    .isSame(dayjs(ref.planEndDate), 'day');

  if (isSameStartDayLastYear && isSameEndDayLastYear) {
    return 'Same period last year';
  }

  if (ref.label?.toLowerCase() === custom.toLowerCase()) {
    return `${dayjs(ref.startDate).format('MMM D, YYYY')} - ${dayjs(ref.endDate).format('MMM D, YYYY')}`;
  }
  if (ref.label?.startsWith(LATEST_WORD)) {
    return `Last ${formatDays(ref.optimizedForDays)}`;
  }
  return ref.label;
}

export function formatOptimizePeriodDisplay(opt: {
  label: string;
  optimizedForDays: number;
  planStartDate?: string;
  planEndDate?: string;
}): string {
  if (!opt) return '';

  if (opt.label?.toLowerCase() === custom.toLowerCase()) {
    if (opt.planStartDate && opt.planEndDate) {
      return `${dayjs(opt.planStartDate).format('MMM D, YYYY')} - ${dayjs(opt.planEndDate).format('MMM D, YYYY')}`;
    }

    return formatDays(opt.optimizedForDays);
  }

  return `Rolling ${formatDays(opt.optimizedForDays)}`;
}

/**
 * Formats a date string into a simple relative time string (e.g., "2 hours ago", "3 days ago").
 * Uses dayjs's built-in relative time formatting.
 *
 * @param dateString - The date string to format
 * @param fallback - Fallback text when dateString is null/undefined
 * @returns Formatted relative time string
 */
export function formatSimpleRelativeTime(
  dateString?: string | null,
  fallback = ''
): string {
  if (!dateString) return fallback;
  return dayjs(dateString).fromNow();
}

/**
 * Formats a date string into a detailed relative time string with granular control.
 * Provides custom formatting for different time ranges (minutes, hours, days, etc.).
 *
 * @param dateString - The date string to format
 * @param fallback - Fallback text when dateString is null/undefined
 * @returns Formatted relative time string with granular time units
 */
export function formatDetailedRelativeTime(
  dateString?: string | null,
  fallback = 'Recently'
): string {
  if (!dateString) {
    return fallback;
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h ago`;
  } else if (diffInMinutes < 10080) {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
}
